import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "../lib/theme";
import { waLink } from "../lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/consultations", label: "Consultations" },
  { to: "/courses", label: "Courses" },
  { to: "/blog", label: "Blog" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

const toolsLinks = [
  { to: "/kundali-generator", label: "Kundali Generator", sub: "Free Vedic birth chart" },
  { to: "/janam-nakshatra", label: "Janam Nakshatra Finder", sub: "Find your birth star & pada" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" aria-label="YourAstroHub Home" className="flex items-center gap-2.5 group">
    <img
      src="/assets/logo-mono.png"
      alt="YourAstroHub - Vedic Astrology Consultation and Education Logo"
      className="h-11 w-auto sm:h-14 lg:h-[62px] drop-shadow-md group-hover:scale-105 transition-transform"
      width="62" height="62"
    />
    <div className="leading-tight">
      <div className="font-serif text-lg sm:text-xl md:text-2xl tracking-tight text-foreground">
        Your<span className="text-[#D4AF37]">Astro</span>Hub
      </div>
      <div className="text-[0.58rem] sm:text-[0.6rem] uppercase tracking-[0.28em] text-[#AD974F]">Your Guide to Happiness</div>
    </div>
  </Link>
);

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const navClass = theme === "dark" ? "glass-nav" : "glass-nav-light";

  return (
    <header data-testid="site-navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "py-2" : "py-3"} ${navClass}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between gap-6">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm rounded-full transition-colors ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-foreground/75 hover:text-[#D4AF37]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {/* Free Tools dropdown */}
          <div className="relative group" data-testid="nav-tools-dropdown">
            <button className="px-3.5 py-2 text-sm rounded-full inline-flex items-center gap-1 text-foreground/75 hover:text-[#D4AF37] transition-colors">
              <Sparkles className="w-3.5 h-3.5" />Free Tools
              <ChevronDown className="w-3 h-3 group-hover:rotate-180 transition-transform" />
            </button>
            <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all absolute left-1/2 -translate-x-1/2 top-full pt-2 w-72 z-50">
              <div className="rounded-2xl border border-foreground/10 bg-background shadow-xl overflow-hidden">
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-foreground/50 bg-[#F4EFE6]/40 dark:bg-[#0F284B]/40">Free Astrology Tools</div>
                {toolsLinks.map((t) => (
                  <NavLink key={t.to} to={t.to}
                    data-testid={`nav-tool-${t.to.slice(1)}`}
                    className="block px-4 py-3 hover:bg-[#D4AF37]/10 border-b border-foreground/5 last:border-0 group/i">
                    <div className="text-sm font-semibold group-hover/i:text-[#D4AF37]">{t.label}</div>
                    <div className="text-[11px] text-foreground/50">{t.sub}</div>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-full grid place-items-center border border-foreground/10 hover:border-[#D4AF37]/60 transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex"
          >
            <Button className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-5">
              Book Consultation
            </Button>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 rounded-full grid place-items-center border border-foreground/10"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-foreground/10 mt-2 pt-2 pb-4 px-6">
          <div className="flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-base ${isActive ? "text-[#D4AF37]" : "text-foreground/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-2 pt-3 border-t border-foreground/10">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />Free Astrology Tools
              </div>
              {toolsLinks.map((t) => (
                <NavLink key={t.to} to={t.to} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-tool-${t.to.slice(1)}`}
                  className="block py-2.5 border-b border-foreground/5 last:border-0">
                  <div className="text-sm font-semibold">{t.label}</div>
                  <div className="text-[11px] text-foreground/50">{t.sub}</div>
                </NavLink>
              ))}
            </div>
            <a href={waLink()} target="_blank" rel="noreferrer" className="mt-3">
              <Button className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
                Book Consultation
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
